import { createSlice } from '@reduxjs/toolkit';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const initialState = {
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  tokenExpiresAtUtc: localStorage.getItem('tokenExpiresAtUtc'),
  user: readJson('authUser'),
  // Filled by GET /auth/me after sign-in: role, tenant and the module matrix.
  access: readJson('authAccess')
};

const persistUser = (user) => {
  localStorage.setItem('authUser', JSON.stringify(user));
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      // A different account means the cached entitlements belong to someone else.
      // (Token refresh keeps the same username, so it does not pay for a refetch.)
      if (state.user && state.user.username !== action.payload.username) {
        state.access = null;
        localStorage.removeItem('authAccess');
      }

      state.token = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.tokenExpiresAtUtc = action.payload.accessTokenExpiresAtUtc;
      state.user = {
        username: action.payload.username,
        role: action.payload.role,
        companyId: action.payload.companyId ?? null,
        mustChangePassword: Boolean(action.payload.mustChangePassword),
        twoFactorSetupRequired: Boolean(action.payload.twoFactorSetupRequired)
      };

      // Keep the cached entitlements in step with the token we just accepted —
      // otherwise a password change would mint a clean token while `access` still
      // said "must change password", bouncing the user back to that screen forever.
      if (state.access) {
        state.access = {
          ...state.access,
          mustChangePassword: state.user.mustChangePassword,
          twoFactorSetupRequired: state.user.twoFactorSetupRequired
        };
        localStorage.setItem('authAccess', JSON.stringify(state.access));
      }

      localStorage.setItem('token', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
      localStorage.setItem('tokenExpiresAtUtc', action.payload.accessTokenExpiresAtUtc);
      persistUser(state.user);
    },
    setAccess: (state, action) => {
      state.access = action.payload;
      localStorage.setItem('authAccess', JSON.stringify(action.payload));

      // /auth/me is the authoritative source once it has answered — the token was
      // minted before the password change and would still say "must change".
      if (state.user) {
        state.user = {
          ...state.user,
          role: action.payload?.role ?? state.user.role,
          companyId: action.payload?.companyId ?? state.user.companyId,
          mustChangePassword: Boolean(action.payload?.mustChangePassword),
          twoFactorEnabled: Boolean(action.payload?.twoFactorEnabled),
          twoFactorSetupRequired: Boolean(action.payload?.twoFactorSetupRequired)
        };
        persistUser(state.user);
      }
    },

    /**
     * Applied after the security screen enrols or removes an authenticator, so the shell
     * stops (or starts) demanding one without a full /auth/me round-trip.
     */
    setTwoFactorState: (state, action) => {
      const enabled = Boolean(action.payload?.enabled);
      const setupRequired = Boolean(action.payload?.setupRequired);

      if (state.user) {
        state.user = { ...state.user, twoFactorEnabled: enabled, twoFactorSetupRequired: setupRequired };
        persistUser(state.user);
      }
      if (state.access) {
        state.access = { ...state.access, twoFactorEnabled: enabled, twoFactorSetupRequired: setupRequired };
        localStorage.setItem('authAccess', JSON.stringify(state.access));
      }
    },
    logout: (state) => {
      state.token = null;
      state.refreshToken = null;
      state.tokenExpiresAtUtc = null;
      state.user = null;
      state.access = null;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiresAtUtc');
      localStorage.removeItem('authUser');
      localStorage.removeItem('authAccess');
    }
  }
});

export const { setCredentials, setAccess, setTwoFactorState, logout } = authSlice.actions;

export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectRefreshToken = (state) => state.auth.refreshToken;
export const selectTokenExpiresAtUtc = (state) => state.auth.tokenExpiresAtUtc;
export const selectAccess = (state) => state.auth.access;
export const selectRole = (state) => state.auth.access?.role ?? state.auth.user?.role ?? null;
export const selectMustChangePassword = (state) =>
  Boolean(state.auth.access?.mustChangePassword ?? state.auth.user?.mustChangePassword);
export const selectTwoFactorSetupRequired = (state) =>
  Boolean(state.auth.access?.twoFactorSetupRequired ?? state.auth.user?.twoFactorSetupRequired);

export default authSlice.reducer;
